import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import PricingModal from '@/components/PricingModal';
import api from '@/utils/api';
import { 
  ChevronDown, 
  ChevronUp, 
  ChevronLeft,
  ChevronRight,
  Menu, 
  X, 
  Zap, 
  Target, 
  FileText, 
  Mail, 
  Megaphone, 
  Palette,
  Check,
  ArrowRight,
  Gift,
  Shield,
  Sparkles,
  Users,
  BarChart3,
  GraduationCap,
  Handshake,
  Play,
  Star,
  Clock,
  Loader2
} from 'lucide-react';

// ════════════════════════════════════════════════════════════════════════════
// BRAND COLORS
// ════════════════════════════════════════════════════════════════════════════
const COLORS = {
  primary: '#4E62F4',
  lime: '#C6FD38',
  lavender: '#C28CFA',
  softBlue: '#95A0F0',
  brightBlue: '#637CE2',
  dark: '#0A0A12',
  darkCard: '#14142B',
  white: '#FFFFFF',
  lightBg: '#F4F5FE',
};

// ════════════════════════════════════════════════════════════════════════════
// ANIMATED BACKGROUNDS - BRAND STYLE
// ════════════════════════════════════════════════════════════════════════════

// Fluid metallic shapes
const FluidShapes = ({ light = false }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div 
      className="absolute -left-40 top-1/4 w-[600px] h-[600px] opacity-40"
      style={{
        background: light 
          ? `radial-gradient(ellipse at 30% 50%, ${COLORS.primary}20 0%, ${COLORS.lavender}10 40%, transparent 70%)`
          : `radial-gradient(ellipse at 30% 50%, ${COLORS.primary}40 0%, ${COLORS.lavender}20 40%, transparent 70%)`,
        filter: 'blur(60px)',
        transform: 'rotate(-15deg)',
        animation: 'float 20s ease-in-out infinite',
      }}
    />
    <div 
      className="absolute -right-40 top-1/3 w-[500px] h-[500px] opacity-30"
      style={{
        background: light
          ? `radial-gradient(ellipse at 70% 50%, ${COLORS.lavender}15 0%, ${COLORS.primary}10 50%, transparent 70%)`
          : `radial-gradient(ellipse at 70% 50%, ${COLORS.lavender}30 0%, ${COLORS.primary}20 50%, transparent 70%)`,
        filter: 'blur(80px)',
        transform: 'rotate(15deg)',
        animation: 'float 25s ease-in-out infinite reverse',
      }}
    />
  </div>
);

// Glowing curved lines background
const GlowingLines = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
    <svg className="absolute w-full h-full" viewBox="0 0 1000 600" preserveAspectRatio="none">
      <defs>
        <linearGradient id="lineGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={COLORS.primary} stopOpacity="0" />
          <stop offset="50%" stopColor={COLORS.softBlue} stopOpacity="0.8" />
          <stop offset="100%" stopColor={COLORS.lavender} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path 
        d="M0,300 Q250,100 500,300 T1000,300" 
        fill="none" 
        stroke="url(#lineGradient1)" 
        strokeWidth="2"
        className="animate-draw-line"
      />
    </svg>
  </div>
);

// Glass card with glow border
const GlassCard = ({ children, className = '', glowColor = COLORS.primary, noPadding = false, dark = true }) => (
  <div className={`relative ${className}`}>
    <div 
      className="absolute -inset-[1px] rounded-2xl opacity-60 blur-[2px]"
      style={{ background: `linear-gradient(135deg, ${glowColor}, ${COLORS.lavender}, ${glowColor})` }}
    />
    <div 
      className={`relative rounded-2xl backdrop-blur-xl ${noPadding ? '' : 'p-6'}`}
      style={{ 
        background: dark 
          ? `linear-gradient(135deg, ${COLORS.darkCard}f0 0%, ${COLORS.dark}e0 100%)`
          : `linear-gradient(135deg, ${COLORS.white}f0 0%, ${COLORS.lightBg}e0 100%)`,
        boxShadow: `0 0 40px ${glowColor}20, inset 0 1px 0 rgba(255,255,255,0.1)`,
      }}
    >
      {children}
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// COUNTDOWN TIMER - PROMINENT VERSION
// ════════════════════════════════════════════════════════════════════════════
const CountdownTimer = ({ targetDate, size = 'normal', dark = true }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const diff = target - now;
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  const isLarge = size === 'large';
  const textColor = dark ? 'text-white' : 'text-gray-800';
  const subTextColor = dark ? 'text-white/50' : 'text-gray-500';

  return (
    <div className="flex gap-2 sm:gap-3 justify-center">
      {[
        { value: timeLeft.days, label: 'días' },
        { value: timeLeft.hours, label: 'horas' },
        { value: timeLeft.minutes, label: 'min' },
        { value: timeLeft.seconds, label: 'seg' }
      ].map((item) => (
        <div key={item.label} className="text-center">
          <div 
            className={`${isLarge ? 'w-16 h-16 sm:w-20 sm:h-20' : 'w-14 h-14 sm:w-16 sm:h-16'} rounded-xl flex items-center justify-center mb-1 backdrop-blur-sm`}
            style={{ 
              background: dark 
                ? `linear-gradient(135deg, ${COLORS.primary}30, ${COLORS.lavender}20)`
                : `linear-gradient(135deg, ${COLORS.primary}15, ${COLORS.lavender}10)`,
              boxShadow: `0 0 20px ${COLORS.primary}30, inset 0 1px 0 rgba(255,255,255,0.1)`,
            }}
          >
            <span className={`${isLarge ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'} font-bold ${textColor} font-mono`}>
              {String(item.value).padStart(2, '0')}
            </span>
          </div>
          <span className={`text-xs ${subTextColor}`}>{item.label}</span>
        </div>
      ))}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// STEPS CAROUSEL
// ════════════════════════════════════════════════════════════════════════════
const StepsCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef(null);

  const steps = [
    { 
      num: 1, 
      title: 'Elige tu estrategia', 
      desc: 'Selecciona entre 14 tipos de funnel adaptados a tu negocio',
      image: '/carousel/step1.jpg'
    },
    { 
      num: 2, 
      title: 'Configura tu funnel', 
      desc: 'Personaliza las variantes y los recursos que necesitas',
      image: '/carousel/step2.jpg'
    },
    { 
      num: 3, 
      title: 'Crea tu proyecto', 
      desc: 'Genera un nuevo funnel con toda la estructura lista',
      image: '/carousel/step3.jpg'
    },
    { 
      num: 4, 
      title: 'Conecta integraciones', 
      desc: 'Exporta a GoHighLevel, Meta Ads y Google Ads',
      image: '/carousel/step4.jpg'
    },
    { 
      num: 5, 
      title: 'Diseña creativos', 
      desc: 'Genera imágenes y vídeos con IA para tus anuncios',
      image: '/carousel/step5.jpg'
    },
    { 
      num: 6, 
      title: 'Lanza campañas', 
      desc: 'Configura y despliega tus campañas de Meta y Google Ads',
      image: '/carousel/step6.jpg'
    },
  ];

  const nextSlide = useCallback(() => setCurrentSlide((prev) => (prev + 1) % steps.length), [steps.length]);
  const prevSlide = useCallback(() => setCurrentSlide((prev) => (prev - 1 + steps.length) % steps.length), [steps.length]);

  useEffect(() => {
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  return (
    <div className="relative max-w-5xl mx-auto">
      {/* Main carousel */}
      <div className="relative overflow-hidden rounded-2xl" ref={carouselRef}>
        <div 
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {steps.map((step) => (
            <div key={step.title} className="w-full flex-shrink-0 px-4">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ boxShadow: `0 0 60px ${COLORS.primary}30` }}>
                {/* Step badge */}
                <div className="absolute top-4 left-4 z-10 flex items-center gap-3 bg-black/60 backdrop-blur-sm rounded-full px-4 py-2">
                  <span 
                    className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
                    style={{ background: COLORS.lime, color: COLORS.dark }}
                  >
                    {step.num}
                  </span>
                  <div>
                    <p className="text-white font-semibold text-sm">{step.title}</p>
                    <p className="text-white/60 text-xs">{step.desc}</p>
                  </div>
                </div>
                {/* Screenshot */}
                <img 
                  src={step.image} 
                  alt={step.title}
                  className="w-full h-auto"
                  style={{ aspectRatio: '16/9', objectFit: 'cover' }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation buttons */}
      <button 
        onClick={prevSlide}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 rounded-full flex items-center justify-center backdrop-blur-sm transition-all hover:scale-110"
        style={{ background: `${COLORS.primary}80`, boxShadow: `0 0 20px ${COLORS.primary}50` }}
      >
        <ChevronLeft className="w-6 h-6 text-white" />
      </button>
      <button 
        onClick={nextSlide}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 rounded-full flex items-center justify-center backdrop-blur-sm transition-all hover:scale-110"
        style={{ background: `${COLORS.primary}80`, boxShadow: `0 0 20px ${COLORS.primary}50` }}
      >
        <ChevronRight className="w-6 h-6 text-white" />
      </button>

      {/* Dots indicator */}
      <div className="flex justify-center gap-2 mt-6">
        {steps.map((step, idx) => (
          <button
            key={`dot-${step.title}`}
            onClick={() => setCurrentSlide(idx)}
            className={`h-2 rounded-full transition-all ${
              idx === currentSlide ? 'w-8' : 'w-2 opacity-40'
            }`}
            style={{ background: idx === currentSlide ? COLORS.lime : COLORS.softBlue }}
          />
        ))}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// FAQ ACCORDION
// ════════════════════════════════════════════════════════════════════════════
const FAQItem = ({ question, answer, isOpen, onClick, dark = true }) => {
  const textColor = dark ? 'text-white/90' : 'text-gray-800';
  const answerColor = dark ? 'text-white/60' : 'text-gray-600';
  const borderColor = dark ? 'border-white/10' : 'border-gray-200';
  
  return (
    <div className={`border-b ${borderColor} last:border-0`}>
      <button onClick={onClick} className="w-full py-5 flex items-center justify-between text-left group">
        <span className={`font-medium ${textColor} group-hover:opacity-100 transition-colors pr-4`}>
          {question}
        </span>
        <div 
          className="w-8 h-8 rounded-full flex items-center justify-center transition-all"
          style={{ 
            background: isOpen ? COLORS.primary : (dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'),
            boxShadow: isOpen ? `0 0 15px ${COLORS.primary}50` : 'none',
          }}
        >
          {isOpen ? <ChevronUp className="w-4 h-4 text-white" /> : <ChevronDown className={`w-4 h-4 ${dark ? 'text-white/50' : 'text-gray-400'}`} />}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}>
        <p className={`${answerColor} leading-relaxed`}>{answer}</p>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PURCHASE MODAL - Coming soon (launches Dec 24)
// ════════════════════════════════════════════════════════════════════════════
const PurchaseModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <GlassCard className="relative max-w-md w-full text-center">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white/60">
          <X className="w-5 h-5" />
        </button>
        <div 
          className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6"
          style={{ 
            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.lavender})`,
            boxShadow: `0 0 30px ${COLORS.primary}50`,
          }}
        >
          <Clock className="w-10 h-10 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">Acceso el 24 de junio</h3>
        <p className="text-white/50 mb-6">
          El acceso a FASCINAI ONE estará disponible a partir del 24 de junio. ¡Vuelve pronto!
        </p>
        <Button 
          onClick={onClose} 
          className="w-full font-semibold py-4 text-lg border-0"
          style={{ background: COLORS.primary, color: 'white' }}
        >
          Entendido
        </Button>
      </GlassCard>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN LANDING
// ════════════════════════════════════════════════════════════════════════════
const Landing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [defaultPlan, setDefaultPlan] = useState('creador');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  const targetDate = '2026-07-02T23:59:00';
  const [bonusExpired] = useState(new Date() > new Date(targetDate));
  const isFounders = !bonusExpired;

  // Handle direct checkout URLs from emails (e.g. ?checkout=creador-monthly)
  useEffect(() => {
    const checkoutParam = searchParams.get('checkout');
    if (checkoutParam) {
      const [plan, interval] = checkoutParam.split('-');
      if (plan && interval && ['creador', 'agencia'].includes(plan) && ['monthly', 'yearly'].includes(interval)) {
        handleDirectCheckout(plan, interval);
      }
    }
  }, [searchParams]);

  const handleDirectCheckout = async (plan, interval) => {
    setCheckoutLoading(true);
    try {
      const res = await api.post('/billing/stripe/create-checkout-session', {
        plan,
        interval,
        origin_url: window.location.origin
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Direct checkout error:', err);
      setCheckoutLoading(false);
      // Open modal as fallback
      setDefaultPlan(plan);
      setPricingModalOpen(true);
    }
  };

  const openPricingModal = (plan = 'creador') => {
    setDefaultPlan(plan);
    setPricingModalOpen(true);
  };

  useEffect(() => {
    if (!loading && isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, loading, navigate]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  if (loading || checkoutLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: COLORS.dark }}>
        <div className="text-center">
          <div className="w-10 h-10 border-3 rounded-full animate-spin mx-auto mb-4" style={{ borderColor: COLORS.primary, borderTopColor: 'transparent' }} />
          {checkoutLoading && <p className="text-white/60">Preparando pago...</p>}
        </div>
      </div>
    );
  }

  const faqs = [
    { q: '¿Necesito saber escribir prompts?', a: 'No. La plataforma ya lleva los prompts dentro — tú solo respondes preguntas sobre tu negocio.' },
    { q: '¿El copy que genera suena genérico?', a: 'No. Antes de escribir, la plataforma aprende tu negocio, tu tono y tu cliente. El resultado suena a tu marca.' },
    { q: '¿Cuántos créditos necesito?', a: 'Un funnel completo (landing + emails + WhatsApp + ads + creativos) usa ~100 créditos. Con Plan Creador tienes 500/mes — para 5 funnels completos o generaciones individuales ilimitadas.' },
    { q: '¿Funciona para mi nicho?', a: 'Sí. Infoproductos, servicios digitales, ecommerce o agencias. Se adapta a tu negocio.' },
    { q: '¿Puedo usarlo para mi agencia?', a: 'Sí. Plan Agencia incluye proyectos ilimitados, white label y multi-usuario.' },
    { q: '¿Hay garantía de devolución?', a: 'No hay permanencia ni contratos. Cancelas cuando quieras sin penalización.' },
  ];

  return (
    <div className="min-h-screen" style={{ background: COLORS.dark }}>
      {/* Global Styles */}
      <style>{`
        @keyframes float { 0%, 100% { transform: translateY(0) rotate(-15deg); } 50% { transform: translateY(-30px) rotate(-10deg); } }
        @keyframes pulse-glow { 0%, 100% { opacity: 0.3; transform: scale(1); } 50% { opacity: 0.6; transform: scale(1.1); } }
        @keyframes gradient-x { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
        @keyframes draw-line { 0% { stroke-dashoffset: 2000; } 100% { stroke-dashoffset: 0; } }
        .animate-gradient-x { background-size: 200% 200%; animation: gradient-x 3s ease infinite; }
        .animate-draw-line { stroke-dasharray: 2000; animation: draw-line 3s ease-out forwards; }
        .script-font { font-family: 'Georgia', serif; font-style: italic; }
      `}</style>

      {/* ══════════════════════════════════════════════════════════════════════
          NAVBAR
          ══════════════════════════════════════════════════════════════════════ */}
      <nav 
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-white/5"
        style={{ background: `${COLORS.dark}e0` }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <img src="/logo-fascinai.png" alt="FASCINAI" className="h-8 w-auto" />
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => navigate('/login')} className="text-sm text-white/60 hover:text-white transition-colors">
                Iniciar sesión
              </button>
              <Button 
                onClick={() => openPricingModal()} 
                className="font-semibold px-6 border-0"
                style={{ background: COLORS.lime, color: COLORS.dark }}
              >
                Ser Founder
              </Button>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-white">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/5 p-4" style={{ background: COLORS.darkCard }}>
            <button onClick={() => navigate('/login')} className="block w-full text-left text-white/60 py-3">Iniciar sesión</button>
            <Button 
              onClick={() => openPricingModal()} 
              className="w-full mt-2 border-0"
              style={{ background: COLORS.lime, color: COLORS.dark }}
            >
              Ser Founder
            </Button>
          </div>
        )}
      </nav>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 1: HERO - Dark with countdown on top
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col items-center pt-20 overflow-hidden" style={{ background: COLORS.dark }}>
        <FluidShapes />
        <GlowingLines />
        
        {/* Countdown bar at top */}
        {!bonusExpired && (
          <div 
            className="w-full py-4 backdrop-blur-sm border-b border-white/10 relative z-10"
            style={{ background: `linear-gradient(90deg, ${COLORS.primary}20, ${COLORS.lavender}20, ${COLORS.primary}20)` }}
          >
            <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" style={{ color: COLORS.lime }} />
                <span className="text-white/80 text-sm font-medium">Oferta Founders cierra en:</span>
              </div>
              <CountdownTimer targetDate={targetDate} size="normal" />
            </div>
          </div>
        )}
        
        <div className="flex-1 flex items-center max-w-5xl mx-auto px-6 py-12 text-center relative z-10">
          <div className="w-full">
            {/* Badge */}
            <div 
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-6 backdrop-blur-sm"
              style={{ 
                background: `${COLORS.lime}15`,
                border: `1px solid ${COLORS.lime}40`,
                boxShadow: `0 0 20px ${COLORS.lime}20`,
              }}
            >
              <Star className="w-4 h-4" style={{ color: COLORS.lime }} />
              <span className="text-sm font-medium" style={{ color: COLORS.lime }}>Acceso anticipado Founders</span>
            </div>

            {/* 🎯 LAUNCH DATE BANNER */}
            <div 
              className="inline-flex items-center gap-3 rounded-xl px-6 py-3 mb-8"
              style={{ 
                background: `linear-gradient(135deg, ${COLORS.primary}40, ${COLORS.lavender}30)`,
                border: `1px solid ${COLORS.primary}50`,
                boxShadow: `0 4px 30px ${COLORS.primary}30`,
              }}
            >
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: COLORS.lime }}
              >
                <Sparkles className="w-5 h-5" style={{ color: COLORS.dark }} />
              </div>
              <div className="text-left">
                <p className="text-white font-bold text-lg">Estreno: 31 de julio de 2026</p>
                <p className="text-white/60 text-sm">Reserva tu plaza Founder hoy · No se te cobra hasta el lanzamiento</p>
              </div>
            </div>

            {/* Main headline */}
            <p 
              className="text-xl sm:text-2xl lg:text-3xl font-bold mb-6 tracking-wide"
              style={{ 
                background: `linear-gradient(90deg, ${COLORS.lime}, ${COLORS.softBlue})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              FASCINAI ONE DISEÑA EL MARKETING DE TU NEGOCIO
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-[1.1]">
              DE IDEA A EMBUDO
              <br />
              <span 
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: `linear-gradient(135deg, ${COLORS.softBlue}, ${COLORS.lavender}, ${COLORS.primary})` }}
              >
                DE VENTAS COMPLETO
              </span>
            </h1>

            {/* Subheadline with lime highlight */}
            <div className="text-2xl sm:text-3xl font-bold mb-8">
              <span style={{ color: COLORS.lime }}>EN 15 MIN.</span>
              <span className="text-white/40 ml-4 text-lg">NO EN 15 DÍAS</span>
            </div>
            
            <p className="text-lg sm:text-xl text-white/60 mb-10 max-w-2xl mx-auto leading-relaxed">
              Landing, emails, WhatsApp, anuncios y creativos — generados con IA, con tu marca, listos para activar.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => scrollTo('precios')} 
                size="lg" 
                className="font-bold text-lg px-10 h-14 group border-0"
                style={{ background: COLORS.lime, color: COLORS.dark }}
              >
                Quiero ser Founder
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                onClick={() => scrollTo('producto')} 
                size="lg" 
                variant="outline" 
                className="text-lg px-10 h-14 border-white/20 text-white hover:bg-white/10"
              >
                <Play className="w-5 h-5 mr-2" /> Ver cómo funciona
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 2: PROBLEMA - White/Light background
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 overflow-hidden" style={{ background: COLORS.white }}>
        <FluidShapes light />
        
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium px-4 py-1 rounded-full mb-6 bg-red-100 text-red-600">
              El problema
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Esto te está frenando
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-12">
            {[
              'Semanas montando el marketing a mano',
              'Freelancers que no llegan a tiempo',
              'Herramientas que no se hablan entre sí',
              'ChatGPT que suena a cualquiera menos a ti'
            ].map((pain) => (
              <div 
                key={pain} 
                className="flex items-start gap-4 p-5 rounded-xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <X className="w-4 h-4 text-red-500" />
                </div>
                <p className="text-gray-700">{pain}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-gray-500 mb-8">
              Lo peor: después de todo ese esfuerzo, el resultado no es el que merece tu negocio.
            </p>
            <div 
              className="inline-block text-white text-xl font-semibold px-8 py-4 rounded-full"
              style={{ 
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.lavender})`,
                boxShadow: `0 0 30px ${COLORS.primary}40`,
              }}
            >
              Hay otra forma →
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 3: SOLUCIÓN - Blue official background
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 overflow-hidden" style={{ background: COLORS.primary }}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/10 rounded-full blur-[80px]" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <span className="inline-block text-sm font-medium px-4 py-1 rounded-full mb-6 bg-white/20 text-white">
            La solución
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Tu funnel listo antes de cenar
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Contestas preguntas sobre tu negocio y antes de que se haga de noche tienes:
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: FileText, text: 'Landing lista para publicar' },
              { icon: Mail, text: 'Emails de secuencia completos' },
              { icon: Megaphone, text: 'WhatsApp por cada fase' },
              { icon: Target, text: 'Copies con headline y CTA' },
              { icon: Palette, text: 'Creativos feed y stories' },
              { icon: Zap, text: 'Todo con tu marca dentro' }
            ].map((item) => (
              <div 
                key={item.text} 
                className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 text-left group hover:bg-white/20 transition-colors"
              >
                <item.icon className="w-6 h-6 mb-3" style={{ color: COLORS.lime }} />
                <p className="text-white font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 4: STEPS CAROUSEL - Dark
          ══════════════════════════════════════════════════════════════════════ */}
      <section id="producto" className="relative py-24 overflow-hidden" style={{ background: COLORS.dark }}>
        <FluidShapes />
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span 
              className="inline-block text-sm font-medium px-4 py-1 rounded-full mb-6"
              style={{ background: `${COLORS.lime}20`, color: COLORS.lime }}
            >
              Así funciona
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              6 pasos y a vender
            </h2>
            <p className="text-white/50 max-w-xl mx-auto">
              Sin curva técnica. Sin prompts. Tú respondes, la plataforma genera.
            </p>
          </div>

          <StepsCarousel />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 5: "NO SOMOS UN ROBOT" - White background
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 overflow-hidden" style={{ background: COLORS.white }}>
        <FluidShapes light />
        
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div 
            className="text-center py-12 px-8 rounded-3xl border"
            style={{ 
              background: `linear-gradient(135deg, ${COLORS.lightBg} 0%, ${COLORS.white} 100%)`,
              borderColor: `${COLORS.primary}20`,
              boxShadow: `0 0 60px ${COLORS.primary}10`,
            }}
          >
            <h2 className="text-5xl sm:text-6xl font-bold mb-4" style={{ color: COLORS.primary }}>NO</h2>
            <p className="text-2xl text-gray-600 script-font mb-2">somos un robot</p>
            <p className="text-2xl text-gray-600 script-font">ni un manual técnico</p>
            
            <div className="mt-8 flex justify-center">
              <div 
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ 
                  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.lavender})`,
                  boxShadow: `0 0 25px ${COLORS.primary}50`,
                }}
              >
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 6: FEATURES - Blue official
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-24" style={{ background: COLORS.primary }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white/20 rounded-full blur-[150px]" />
        </div>
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium px-4 py-1 rounded-full mb-6 bg-white/20 text-white">
              Todo incluido
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Olvídate de 6 herramientas
            </h2>
            <p className="text-white/70 max-w-xl mx-auto">
              Un sistema completo que reemplaza Mailchimp, ManyChat, Canva, ClickFunnels, ChatGPT y Meta Business.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: 'Wizard de 8 pasos', desc: 'Sin curva técnica. Sin prompts. Tú respondes, la plataforma genera.' },
              { icon: Target, title: '14 estrategias', desc: 'Lanzamiento, Evergreen, PLF, High Ticket, SaaS, y más variantes.' },
              { icon: FileText, title: 'Landing pages', desc: 'Editor drag & drop. Export a HTML, WordPress o GHL.' },
              { icon: Mail, title: 'Email + WhatsApp', desc: 'Secuencias completas para cada fase del funnel.' },
              { icon: Megaphone, title: 'Copies para Ads', desc: '7 ángulos, 5 variantes por anuncio. Adaptado a tu nicho.' },
              { icon: Palette, title: 'Creativos IA', desc: 'Imágenes, vídeos, avatares. Formatos 4:5 y 9:16.' }
            ].map((feature) => (
              <div 
                key={feature.title} 
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-white/20">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-white/60 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 7: BONUS - REMOVED (solo quedaba 1 bonus de 29€, no aportaba)
          Los +500 créditos extra se mantienen como parte del plan Founder
          ══════════════════════════════════════════════════════════════════════ */}

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 8: PRECIOS - White background
          ══════════════════════════════════════════════════════════════════════ */}
      <section id="precios" className="relative py-24" style={{ background: COLORS.white }}>
        <FluidShapes light />
        
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <span 
              className="inline-block text-sm font-medium px-4 py-1 rounded-full mb-6"
              style={{ background: `${COLORS.primary}10`, color: COLORS.primary }}
            >
              Price lock de por vida
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Este precio no volverá
            </h2>
            <p className="text-gray-500">Mientras no canceles, pagas este precio. Después sube.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Creador */}
            <div 
              className="rounded-3xl p-8 border shadow-lg hover:shadow-xl transition-shadow"
              style={{ 
                background: COLORS.white,
                borderColor: `${COLORS.primary}20`,
              }}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Plan Creador</h3>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-5xl font-bold text-gray-900">39€</span>
                <span className="text-gray-400">/mes</span>
              </div>
              <div className="flex items-center gap-2 mb-6">
                <span className="text-gray-400 line-through text-sm">49€</span>
                <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: `${COLORS.lime}30`, color: COLORS.dark }}>-20%</span>
              </div>
              
              <ul className="space-y-3 mb-8">
                {['500 créditos/mes', 'Hasta 5 proyectos', 'Wizard completo', 'Todas las integraciones', 'Sin watermark', '+ Bonus Founders'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-gray-600">
                    <Check className="w-5 h-5" style={{ color: COLORS.primary }} /> {item}
                  </li>
                ))}
              </ul>

              <Button 
                onClick={() => openPricingModal()} 
                className="w-full font-semibold h-12 text-base border-0"
                style={{ background: COLORS.primary, color: 'white' }}
              >
                Elegir Creador
              </Button>
            </div>

            {/* Agencia */}
            <div className="relative">
              <span 
                className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-4 py-1 rounded-full z-10"
                style={{ background: COLORS.lime, color: COLORS.dark }}
              >
                POPULAR
              </span>
              <div 
                className="rounded-3xl p-8 border-2 shadow-xl"
                style={{ 
                  background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.lavender} 100%)`,
                  borderColor: COLORS.lime,
                }}
              >
                <h3 className="text-xl font-bold text-white mb-2 mt-2">Plan Agencia</h3>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-5xl font-bold text-white">119€</span>
                  <span className="text-white/60">/mes</span>
                </div>
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-white/40 line-through text-sm">149€</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded bg-white/20 text-white">-20%</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {['2.000 créditos/mes', 'Proyectos ilimitados', 'White label', 'Multi-usuario (5)', 'API access', '+ Bonus Founders'].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-white/90">
                      <Check className="w-5 h-5" style={{ color: COLORS.lime }} /> {item}
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={() => openPricingModal()} 
                  className="w-full font-semibold h-12 text-base border-0"
                  style={{ background: COLORS.lime, color: COLORS.dark }}
                >
                  Elegir Agencia
                </Button>
              </div>
            </div>
          </div>
          
          {/* 🎯 LAUNCH DATE NOTICE */}
          <div className="max-w-2xl mx-auto mt-10 p-6 rounded-2xl text-center" style={{ background: `${COLORS.primary}08`, border: `1px solid ${COLORS.primary}20` }}>
            <div className="flex items-center justify-center gap-3 mb-2">
              <Sparkles className="w-5 h-5" style={{ color: COLORS.primary }} />
              <span className="font-bold text-gray-900">Lanzamiento: 31 de julio de 2026</span>
            </div>
            <p className="text-gray-600 text-sm">
              Al suscribirte hoy aseguras tu plaza Founder al mejor precio. <strong>No se te cobra nada hasta el 31 de julio</strong>, cuando la app esté lista y tengas acceso completo.
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 9: GARANTÍA - Dark
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-20" style={{ background: COLORS.dark }}>
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
            style={{ background: `${COLORS.primary}20` }}
          >
            <Shield className="w-8 h-8" style={{ color: COLORS.primary }} />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">Cancela cuando quieras</h2>
          <p className="text-white/50 leading-relaxed">
            No hay contratos. Cancelas cuando quieras sin formularios, sin llamadas de retención, sin preguntas.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 10: AUTORIDAD - White
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24" style={{ background: COLORS.white }}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Creado por marketers reales</h2>
            <p className="text-gray-500">+10 años ayudando a negocios reales a crecer</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: BarChart3, value: '+3M€', label: 'en tráfico gestionado' },
              { icon: GraduationCap, value: '+4.000', label: 'alumnos formados' },
              { icon: Handshake, value: '∞', label: 'sectores cubiertos' }
            ].map((stat) => (
              <div 
                key={stat.label} 
                className="text-center p-6 rounded-2xl border"
                style={{ 
                  background: COLORS.lightBg,
                  borderColor: `${COLORS.primary}10`,
                }}
              >
                <stat.icon className="w-8 h-8 mx-auto mb-3" style={{ color: COLORS.primary }} />
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 11: PARA QUIÉN - Blue
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24" style={{ background: COLORS.primary }}>
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white text-center mb-12">¿Es para ti?</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Infoproductores', desc: 'Lanzar más seguido, con menos caos.' },
              { title: 'Agencias', desc: 'Entregar más rápido sin contratar más.' },
              { title: 'Emprendedores', desc: 'Un sistema que trabaja por ti.' }
            ].map((avatar) => (
              <div 
                key={avatar.title}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 bg-white/20">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-white mb-1">{avatar.title}</h3>
                <p className="text-sm text-white/60">{avatar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 12: FAQ - White
          ══════════════════════════════════════════════════════════════════════ */}
      <section id="faq" className="py-24" style={{ background: COLORS.white }}>
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 text-center mb-12">Resolvemos tus dudas</h2>
          <div 
            className="rounded-2xl border p-6"
            style={{ 
              background: COLORS.lightBg,
              borderColor: `${COLORS.primary}15`,
            }}
          >
            {faqs.map((faq, idx) => (
              <FAQItem key={faq.q} question={faq.q} answer={faq.a} isOpen={openFAQ === idx} onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)} dark={false} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 13: CTA FINAL - Dark with countdown
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 overflow-hidden" style={{ background: COLORS.dark }}>
        <FluidShapes />
        <GlowingLines />
        
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">
            Tu marketing completo.
          </h2>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-8">
            <span style={{ color: COLORS.lime }}>En una tarde.</span>
          </h2>

          {!bonusExpired && (
            <div className="mb-10">
              <p className="text-white/60 mb-4 text-sm">La oferta Founders cierra el 2 de julio</p>
              <CountdownTimer targetDate={targetDate} size="large" />
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => openPricingModal()} 
              size="lg" 
              className="font-bold px-10 h-14 text-base border-0"
              style={{ background: COLORS.lime, color: COLORS.dark }}
            >
              Creador · 39€/mes
            </Button>
            <Button 
              onClick={() => openPricingModal()} 
              size="lg" 
              className="font-bold px-10 h-14 text-base backdrop-blur-sm"
              style={{ background: `${COLORS.primary}40`, color: 'white', border: `1px solid ${COLORS.primary}60` }}
            >
              Agencia · 119€/mes
            </Button>
          </div>

          <p className="text-white/40 text-sm mt-6">Sin permanencia. Cancela cuando quieras.</p>
          
          {/* 🎯 Launch date reminder */}
          <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
            <Sparkles className="w-4 h-4" style={{ color: COLORS.lime }} />
            <span className="text-white/70 text-sm">Estreno <strong className="text-white">31 de julio</strong> · No se cobra hasta entonces</span>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FOOTER
          ══════════════════════════════════════════════════════════════════════ */}
      <footer className="border-t border-white/5 py-12 px-6" style={{ background: COLORS.dark }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <img src="/logo-fascinai.png" alt="FASCINAI" className="h-6 w-auto opacity-60" />
          <div className="flex gap-6 text-xs text-white/30">
            <a href="/privacy" className="hover:text-white/60 transition-colors">Privacidad</a>
            <a href="/cookies" className="hover:text-white/60 transition-colors">Cookies</a>
            <a href="/terms" className="hover:text-white/60 transition-colors">Términos</a>
            <a href="mailto:soporte@fascinai.pro" className="hover:text-white/60 transition-colors">Contacto</a>
          </div>
          <p className="text-xs text-white/20">© 2026 Fascinads Soluciones SL</p>
        </div>
      </footer>

      <PricingModal isOpen={pricingModalOpen} onClose={() => setPricingModalOpen(false)} defaultPlan={defaultPlan} />
    </div>
  );
};

export default Landing;
